/**
 * CYP ASD SSI Review — Diagnostic criteria review page.
 *
 * Two-panel layout modelled after the Adult ADHD "Recommended Diagnosis" page:
 *
 *   Left panel  — read-only summary table showing each symptom criterion
 *                  (A1–B4), the evidence decisions made for Early Developmental
 *                  Period and Current, and which functional domains were flagged.
 *
 *   Right panel — the five DSM-5 top-level criteria (A–E) with Yes/No buttons.
 *                  Criteria A and B are auto-prefilled based on the symptom
 *                  evidence recorded during the SSI. Criteria C, D, and E
 *                  require manual clinician input.
 *
 * This is a frontend-only mockup — state is received via route location state
 * from the SSI page (no Firestore persistence).
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { SearchIcon, XIcon, PlusIcon } from '@heroicons/react/solid';
import { useVideoStream } from '../../../context/VideoStreamContext';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import CypAsdSSISideNavbar from './components/CypAsdSSISideNavbar';
import StructuredNotesEditor from '../12_observation/components/StructuredNotesEditor';
import SSI_CRITERIA, { FUNCTIONAL_IMPAIRMENT_DOMAINS } from './config/cypAsdSSIConfig';
import { EMPTY_SSI_REVIEW_DATA, ensureSeedData } from '../seedData/cypAsdSeedData';
import { renderNoteWithQuotes } from '../../../utils/highlightQuotes';

/** Confidence increments available for the diagnostic confidence meter. */
const CONFIDENCE_STEPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/**
 * Curated conditions for the Formulation panel. Autism is pinned to the top;
 * the rest cover common differentials / co-occurring conditions in CYP ASD
 * pathways. Mirrors the list used in ObservationSummary for consistency.
 */
const FORMULATION_CONDITIONS = [
  'Autism Spectrum Disorder (ASD)',
  'Attention Deficit Hyperactivity Disorder (ADHD)',
  'Social Anxiety Disorder',
  'Generalised Anxiety Disorder',
  'Developmental Speech or Language Disorders',
  'Developmental Learning Disorder',
  'Disorders of Intellectual Development',
  'Reactive Attachment Disorder',
  'Obsessive-Compulsive Disorder',
  'Post-Traumatic Stress Disorder',
  'Depressive Disorders',
  'Personality Disorder',
];

/**
 * Normalises notes into a flat array of `{ id, text }` objects, filtering out
 * empty entries. Handles both the StructuredNotesEditor array format and
 * legacy HTML/plain-text strings (which become a single-element array).
 */
function normaliseNotesArray(notesValue) {
  if (!notesValue) return [];
  if (Array.isArray(notesValue)) {
    return notesValue.filter(n => n && String(n.text || '').trim());
  }
  const trimmed = String(notesValue).trim();
  if (!trimmed) return [];
  return [{ id: 'legacy', text: trimmed }];
}

/**
 * Maps evidence text values to compact coloured pills for the summary table.
 * Follows the same traffic-light palette used in the criterion panel.
 */
/**
 * Maps evidence text values to coloured pills for the summary table.
 * Uses the same colour scheme as the selected evidence buttons in
 * CypAsdCriterionPanel so there is visual parity between the SSI
 * input and the review summary.
 */
const EVIDENCE_PILL = {
  'Clear evidence':   { label: 'Clear evidence',   bg: 'bg-emerald-600', text: 'text-white'   },
  'Partial evidence': { label: 'Partial evidence',  bg: 'bg-amber-500',   text: 'text-white'   },
  'No evidence':      { label: 'No evidence',       bg: 'bg-red-500',     text: 'text-white'   },
  'Inconclusive':     { label: 'Inconclusive',      bg: 'bg-gray-500',    text: 'text-white'   },
};

function EvidencePill({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  const style = EVIDENCE_PILL[value] || { label: value, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-block w-[7.5rem] text-center px-2.5 py-0.5 rounded-lg text-[11px] font-semibold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

/**
 * Derives whether Criterion A is met:
 * All three of A1, A2, A3 must have "Clear evidence" in BOTH life stages.
 */
function prefillCriterionA(allData) {
  const aIds = ['A1', 'A2', 'A3'];
  const allDecided = aIds.every(id => {
    const d = allData[id];
    return d?.evidence_early && d?.evidence_current;
  });
  if (!allDecided) return null;

  return aIds.every(id => {
    const d = allData[id];
    return d.evidence_early === 'Clear evidence' && d.evidence_current === 'Clear evidence';
  });
}

/**
 * Derives whether Criterion B is met:
 * At least 2 of B1–B4 must have "Clear evidence" in BOTH life stages.
 */
function prefillCriterionB(allData) {
  const bIds = ['B1', 'B2', 'B3', 'B4'];
  const allDecided = bIds.every(id => {
    const d = allData[id];
    return d?.evidence_early && d?.evidence_current;
  });
  if (!allDecided) return null;

  const metCount = bIds.filter(id => {
    const d = allData[id];
    return d.evidence_early === 'Clear evidence' && d.evidence_current === 'Clear evidence';
  }).length;

  return metCount >= 2;
}

/**
 * Shared column for the stacked Review page so the DSM / domain tables and
 * the Confidence / Differentials / Notes panels are the same width. Matches
 * Adult SSI Review and Observation Review (`max-w-5xl`).
 */
const REVIEW_COLUMN_CLASS = 'w-full max-w-5xl';

export default function CypAsdSSIReview() {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const videoStream = useVideoStream();

  useEffect(() => { ensureSeedData(patientId); }, [patientId]);

  // Opt this page into the floating video panel while it's mounted
  useEffect(() => {
    videoStream?.setVideoEnabled(true);
    return () => videoStream?.setVideoEnabled(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Session-storage keys — match the SSI page key for symptom data. */
  const ssiKey = `cyp-asd-ssi-${patientId}`;
  const reviewKey = `cyp-asd-ssi-review-${patientId}`;

  /**
   * Symptom data — prefer localStorage (always up-to-date) over
   * location.state (only set when navigating from the SSI page directly).
   */
  const allData = useMemo(() => {
    try {
      const cached = localStorage.getItem(ssiKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return location.state?.allData || {};
  }, [ssiKey, location.state]);

  // ---- Criteria decisions (right panel) ----
  const criterionAPrefill = useMemo(() => prefillCriterionA(allData), [allData]);
  const criterionBPrefill = useMemo(() => prefillCriterionB(allData), [allData]);

  /**
   * Review-specific state (criteria decisions, confidence, notes).
   * Initialised from localStorage so values survive a round-trip
   * back to the SSI page and return.
   */
  const savedReview = useMemo(() => {
    try {
      const cached = localStorage.getItem(reviewKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_SSI_REVIEW_DATA;
  }, [reviewKey]);

  const [decisions, setDecisions] = useState(() => savedReview?.decisions ?? {
    A: criterionAPrefill,
    B: criterionBPrefill,
    C: null,
    D: null,
    E: null,
  });

  // eslint-disable-next-line no-unused-vars -- kept for future MDT Review page
  const handleDecision = (criterion, value) => {
    setDecisions(prev => ({
      ...prev,
      [criterion]: prev[criterion] === value ? null : value,
    }));
  };

  // ---- Confidence level (0–100%, increments of 10) ----
  const [confidence, setConfidence] = useState(() => savedReview?.confidence ?? null);

  // ---- Confidence ternary (3-button alternative) ----
  const [confidenceTernary, setConfidenceTernary] = useState(() => savedReview?.confidenceTernary ?? null);

  /**
   * Design toggle — allows switching between the 0–100% segmented bar
   * and the 5-option scale confidence input so stakeholders can compare
   * both designs. Defaults to the 5-option scale for parity with Observation.
   */
  // eslint-disable-next-line no-unused-vars
  const [confidenceStyle, _setConfidenceStyle] = useState(() => savedReview?.confidenceStyle ?? 'ternary');

  // ---- Internal case notes (structured bullet notes) ----
  const [formulationNotes, setFormulationNotes] = useState(() => {
    const raw = savedReview?.formulationNotes;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) return [{ id: Date.now(), text: raw.trim(), type: 'note' }];
    return [];
  });
  const handleNotesChange = useCallback((val) => setFormulationNotes(val), []);

  /** Formulation — which conditions the clinician is considering + per-condition notes. */
  const [formulationConditions, setFormulationConditions] = useState(
    () => savedReview?.formulationConditions ?? [],
  );
  const [formulationConditionNotes, setFormulationConditionNotes] = useState(
    () => savedReview?.formulationConditionNotes ?? {},
  );

  /**
   * Tracks conditions that are in the process of being removed — they stay
   * in the list long enough to play the exit animation, then get purged.
   */
  const [leavingConditions, setLeavingConditions] = useState(new Set());

  const toggleCondition = useCallback((condition) => {
    if (formulationConditions.includes(condition)) {
      setLeavingConditions(prev => new Set(prev).add(condition));
    } else {
      setFormulationConditions(prev => [...prev, condition]);
    }
  }, [formulationConditions]);

  const finishLeaving = useCallback((condition) => {
    setLeavingConditions(prev => {
      const next = new Set(prev);
      next.delete(condition);
      return next;
    });
    setFormulationConditions(prev => prev.filter(c => c !== condition));
  }, []);

  const handleConditionNoteChange = useCallback((condition, value) => {
    setFormulationConditionNotes(prev => ({ ...prev, [condition]: value }));
  }, []);

  /**
   * Moves a note card from one formulation condition to another. The
   * StructuredNotesEditor fires this when a note is dragged between rows,
   * enabling clinicians to quickly reorganise thoughts across conditions.
   */
  const handleMoveFormulationNote = useCallback((sourceCondition, noteId, targetCondition, insertAt) => {
    setFormulationConditionNotes(prev => {
      const sourceNotes = Array.isArray(prev[sourceCondition]) ? prev[sourceCondition] : [];
      const targetNotes = Array.isArray(prev[targetCondition]) ? prev[targetCondition] : [];
      const note = sourceNotes.find(n => n.id === noteId);
      if (!note) return prev;
      const updated = [...targetNotes];
      if (insertAt != null && insertAt <= updated.length) {
        updated.splice(insertAt, 0, note);
      } else {
        updated.push(note);
      }
      return {
        ...prev,
        [sourceCondition]: sourceNotes.filter(n => n.id !== noteId),
        [targetCondition]: updated,
      };
    });
  }, []);

  /** "Other" condition — tracks whether the inline input is open and its draft text. */
  const [otherInputOpen, setOtherInputOpen] = useState(false);
  const [otherDraft, setOtherDraft] = useState('');
  const otherInputRef = useRef(null);

  /**
   * Custom conditions typed via the "Other" input. These are rendered as
   * additional pill buttons alongside the pre-defined conditions so the
   * user can toggle them on/off just like any built-in condition.
   */
  const [customConditions, setCustomConditions] = useState(
    () => savedReview?.customConditions ?? [],
  );

  const confirmOtherCondition = useCallback(() => {
    const trimmed = otherDraft.trim();
    if (trimmed && !formulationConditions.includes(trimmed) && !FORMULATION_CONDITIONS.includes(trimmed)) {
      setCustomConditions(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
      setFormulationConditions(prev => [...prev, trimmed]);
    }
    setOtherDraft('');
    setOtherInputOpen(false);
  }, [otherDraft, formulationConditions]);

  /** Persist review-specific state to localStorage on every change. */
  useEffect(() => {
    try {
      localStorage.setItem(reviewKey, JSON.stringify({
        decisions,
        confidence,
        confidenceTernary,
        confidenceStyle,
        formulationNotes,
        formulationConditions,
        formulationConditionNotes,
        customConditions,
      }));
    } catch { /* quota exceeded — ignore in mockup */ }
  }, [reviewKey, decisions, confidence, confidenceTernary, confidenceStyle, formulationNotes, formulationConditions, formulationConditionNotes, customConditions]);

  // ---- Summary data for the left table ----
  const symptomRows = useMemo(() =>
    SSI_CRITERIA.filter(c => c.available).map(c => {
      const d = allData[c.id] || {};
      return {
        id: c.id,
        shortTitle: c.shortTitle,
        fullTitle: c.fullTitle,
        earlyEvidence: d.evidence_early || null,
        currentEvidence: d.evidence_current || null,
        domainsEarly: d.domains_early || [],
        domainsCurrent: d.domains_current || [],
        notesEarly: normaliseNotesArray(d.notes_early),
        notesCurrent: normaliseNotesArray(d.notes_current),
        askedQuestions: d.asked_questions || [],
      };
    }),
  [allData]);


  /**
   * Domain impact summary — for each functional impairment domain, derive
   * the highest evidence level across all symptoms for each life stage.
   *
   * Priority order: Clear evidence > Partial evidence > No evidence > Inconclusive.
   * If at least one symptom with "Clear evidence" flagged a domain for a
   * given stage, that domain is "Clear evidence" for that stage, etc.
   */
  const EVIDENCE_PRIORITY = ['Clear evidence', 'Partial evidence', 'No evidence', 'Inconclusive'];

  const domainRows = useMemo(() =>
    FUNCTIONAL_IMPAIRMENT_DOMAINS.map(domain => {
      const earlySymptoms = {};
      const currentSymptoms = {};

      EVIDENCE_PRIORITY.forEach(lvl => { earlySymptoms[lvl] = []; currentSymptoms[lvl] = []; });

      symptomRows.forEach(row => {
        if (row.domainsEarly?.includes(domain.id) && row.earlyEvidence) {
          earlySymptoms[row.earlyEvidence]?.push(`${row.id}. ${row.shortTitle}`);
        }
        if (row.domainsCurrent?.includes(domain.id) && row.currentEvidence) {
          currentSymptoms[row.currentEvidence]?.push(`${row.id}. ${row.shortTitle}`);
        }
      });

      const deriveLevel = (buckets) => {
        for (const lvl of EVIDENCE_PRIORITY) {
          if (buckets[lvl].length > 0) return lvl;
        }
        return null;
      };

      return {
        domain,
        earlyLevel: deriveLevel(earlySymptoms),
        currentLevel: deriveLevel(currentSymptoms),
        earlySymptoms,
        currentSymptoms,
      };
    }),
    [symptomRows], // eslint-disable-line react-hooks/exhaustive-deps -- EVIDENCE_PRIORITY is a module-level constant
  );

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['CYP ASD Assessment'],
    ['Diagnostic Developmental History', `/patient/${patientId}/proforma/cyp-asd/semi-structured-interview`],
    ['Review'],
  ];

  /** The five DSM-5 top-level criteria — kept for future MDT Review page */
  // eslint-disable-next-line no-unused-vars
  const CRITERIA_PANELS = [
    {
      key: 'A',
      title: 'Criterion A — Social Communication & Interaction',
      description: 'Persistent deficits in social communication and social interaction across multiple contexts, as manifested by all three sub-criteria (A1, A2, A3).',
      prefilled: criterionAPrefill !== null,
    },
    {
      key: 'B',
      title: 'Criterion B — Restricted, Repetitive Behaviour',
      description: 'Restricted, repetitive patterns of behaviour, interests, or activities, as manifested by at least two of the four sub-criteria (B1–B4).',
      prefilled: criterionBPrefill !== null,
    },
    {
      key: 'C',
      title: 'Criterion C — Age 4–5',
      description: 'Symptoms must be present in the early developmental period (but may not become fully manifest until social demands exceed limited capacities, or may be masked by learned strategies in later life).',
      prefilled: false,
    },
    {
      key: 'D',
      title: 'Criterion D — Functional Impairment',
      description: 'Symptoms cause clinically significant impairment in social, occupational, or other important areas of current functioning.',
      prefilled: false,
    },
    {
      key: 'E',
      title: 'Criterion E — Exclusion',
      description: 'These disturbances are not better explained by intellectual disability or global developmental delay. Social communication should be below that expected for general developmental level.',
      prefilled: false,
    },
  ];

  /**
   * Detail popup state — opens an overlay with richer context for a
   * symptom or domain cell. Two shapes:
   *
   * Symptom: { type: 'symptom', symptomId, shortTitle, stage,
   *            askedQuestions, quotes, notes }
   * Domain:  { type: 'domain', domainTitle, stage, symptomBuckets }
   */
  const [detailPopup, setDetailPopup] = useState(null);

  /**
   * Sidebar navigation handler — clicking a criterion navigates back to
   * the SSI page with that criterion active. Clicking 'REVIEW' is a no-op
   * since we're already on the review page.
   */
  const handleSidebarNavigate = useCallback((itemId) => {
    if (itemId === 'REVIEW') return;
    navigate(
      `/patient/${patientId}/proforma/cyp-asd/semi-structured-interview`,
      { state: { allData, activeItemId: itemId } },
    );
  }, [navigate, patientId, allData]);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      <div className="flex h-[calc(100vh-8rem)]">
        {/* Left sidebar — same as SSI page, with Review highlighted */}
        <CypAsdSSISideNavbar
          criteria={SSI_CRITERIA}
          activeItemId="REVIEW"
          onSelectItem={handleSidebarNavigate}
          criterionData={allData}
          sidebarMode="compact"
        />

        {/* Main panel — scrollable review content */}
        <div className="flex-1 min-w-0 overflow-y-auto p-6 text-left">

        {/* Page header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Review of Diagnostic Developmental History
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Summary of evidence across Age 4–5 and Current presentation by DSM criteria.
          </p>
        </div>

        {/* Stacked centred layout — table on top, review panel below */}
        <div className="flex flex-col items-center gap-6">

          {/* ======== Summary table ======== */}
          <div className={REVIEW_COLUMN_CLASS}>
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full text-left">
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '30%' }} />
              </colgroup>
              <thead>
                <tr className="bg-primary text-white">
                  <th className="p-3 text-sm font-bold">DSM Criteria</th>
                  <th className="p-3 text-sm font-bold">Age 4–5</th>
                  <th className="p-3 text-sm font-bold">Current</th>
                </tr>
              </thead>
              <tbody>
                {symptomRows.map((row, i) => {
                  const bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                  const renderStageCell = (evidence, notes, stageLabel) => {
                    const hasDetail = notes.length > 0 || row.askedQuestions.length > 0;
                    return (
                      <td className="p-3 align-top">
                        <div className="flex items-center gap-1.5">
                          <EvidencePill value={evidence} />
                          {hasDetail && (
                            <button
                              onClick={() => setDetailPopup({
                                type: 'symptom',
                                symptomId: row.id,
                                shortTitle: row.shortTitle,
                                stage: stageLabel,
                                evidence,
                                askedQuestions: row.askedQuestions,
                                notes,
                              })}
                              className="text-gray-400 hover:text-primary transition-colors duration-150"
                              title="View details"
                            >
                              <SearchIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  };

                  return (
                    <tr key={row.id} className={`${bgClass} border-b border-gray-200`}>
                      <td className="p-3 align-top">
                        <p className="text-sm text-gray-900 font-bold leading-snug">
                          {row.id}. {row.shortTitle}
                        </p>
                      </td>
                      {renderStageCell(row.earlyEvidence, row.notesEarly, 'Age 4\u20135')}
                      {renderStageCell(row.currentEvidence, row.notesCurrent, 'Current')}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {/* ======== Domain impact table ======== */}
          <div className={REVIEW_COLUMN_CLASS}>
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full text-left">
                <colgroup>
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '30%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-3 text-sm font-bold">Domain</th>
                    <th className="p-3 text-sm font-bold">Age 4–5</th>
                    <th className="p-3 text-sm font-bold">Current</th>
                  </tr>
                </thead>
                <tbody>
                  {domainRows.map((row, i) => {
                    const bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                    const renderDomainStageCell = (level, symptomBuckets, stageLabel) => {
                      const hasAny = EVIDENCE_PRIORITY.some(lvl => symptomBuckets[lvl]?.length > 0);
                      return (
                        <td className="p-3 align-top">
                          <div className="flex items-center gap-1.5">
                            <EvidencePill value={level} />
                            {hasAny && (
                              <button
                                onClick={() => setDetailPopup({
                                  type: 'domain',
                                  domainTitle: row.domain.title,
                                  stage: stageLabel,
                                  symptomBuckets,
                                })}
                                className="text-gray-400 hover:text-primary transition-colors duration-150"
                                title="View breakdown"
                              >
                                <SearchIcon className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    };

                    return (
                      <tr key={row.domain.id} className={`${bgClass} border-b border-gray-200 last:border-b-0`}>
                        <td className="p-3 align-top">
                          <p className="text-sm text-gray-900 font-bold leading-snug">
                            {row.domain.title}
                          </p>
                        </td>
                        {renderDomainStageCell(row.earlyLevel, row.earlySymptoms, 'Age 4–5')}
                        {renderDomainStageCell(row.currentLevel, row.currentSymptoms, 'Current')}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ======== Review panel (Confidence) ======== */}
          <div className={`${REVIEW_COLUMN_CLASS} bg-gray-50 rounded-2xl border border-gray-200 p-5 space-y-1`}>

            {/*
              * DSM-5 Criteria Yes/No buttons — commented out for now.
              * Will be ported to a dedicated MDT Review page in the future.
              *
              * {CRITERIA_PANELS.map((panel, idx) => (
              *   <div
              *     key={panel.key}
              *     className={`${idx > 0 ? 'border-t border-gray-200 pt-3' : ''} pb-3`}
              *   >
              *     <div className="flex items-start gap-4">
              *       <div className="flex-1 min-w-0">
              *         <h3 className="text-sm font-bold text-gray-800 mb-0.5">{panel.title}<span className="text-red-500 ml-0.5">*</span></h3>
              *         <p className="text-xs text-gray-500 leading-relaxed">{panel.description}</p>
              *         {panel.prefilled && (
              *           <p className="text-[11px] text-gray-400 italic mt-1">
              *             Pre-filled from symptom evidence decisions
              *           </p>
              *         )}
              *       </div>
              *       <div className="flex gap-2 flex-shrink-0 pt-0.5">
              *         <button
              *           onClick={() => handleDecision(panel.key, true)}
              *           className={`
              *             w-16 h-9 rounded-lg border-2 text-sm font-semibold transition-all duration-200
              *             ${decisions[panel.key] === true
              *               ? 'bg-emerald-100 border-emerald-400 text-emerald-700'
              *               : 'bg-white border-gray-300 text-gray-500 hover:border-emerald-300 hover:text-emerald-600'}
              *           `}
              *         >
              *           Yes
              *         </button>
              *         <button
              *           onClick={() => handleDecision(panel.key, false)}
              *           className={`
              *             w-16 h-9 rounded-lg border-2 text-sm font-semibold transition-all duration-200
              *             ${decisions[panel.key] === false
              *               ? 'bg-red-100 border-red-400 text-red-700'
              *               : 'bg-white border-gray-300 text-gray-500 hover:border-red-300 hover:text-red-600'}
              *           `}
              *         >
              *           No
              *         </button>
              *       </div>
              *     </div>
              *   </div>
              * ))}
              */}

            {/* ---- Confidence Level ---- */}
            <div className="pt-1">
              <h3 className="text-sm font-bold text-gray-800 mb-0.5">
                Confidence Level<span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                Based on the Diagnostic Developmental History, how likely is it in your clinical opinion that this person is autistic?
              </p>

              {confidenceStyle === 'percentage' ? (
                <div className="flex items-center gap-0.5 mb-2">
                  {CONFIDENCE_STEPS.map((step) => {
                    const isSelected = confidence !== null && step <= confidence;
                    const isExact = step === confidence;

                    let selectedColour = 'bg-red-400';
                    if (step >= 40 && step < 70) selectedColour = 'bg-amber-400';
                    if (step >= 70) selectedColour = 'bg-emerald-400';

                    return (
                      <button
                        key={step}
                        onClick={() => setConfidence(confidence === step ? null : step)}
                        className={`
                          flex-1 h-7 first:rounded-l-md last:rounded-r-md
                          font-bold transition-all duration-200
                          ${isSelected
                            ? `${selectedColour} text-white`
                            : 'bg-gray-200 text-gray-400 hover:bg-gray-300'}
                          ${isExact ? 'ring-2 ring-offset-1 ring-primary z-10 relative text-xs' : 'text-[10px]'}
                        `}
                        title={`${step}%`}
                      >
                        {step}%
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2">
                  {[
                    { value: 'very_unlikely', label: 'Very Unlikely',     activeBg: 'bg-red-100 border-red-400 text-red-700',         hoverBorder: 'hover:border-red-300' },
                    { value: 'unlikely',      label: 'Unlikely',          activeBg: 'bg-orange-100 border-orange-400 text-orange-700', hoverBorder: 'hover:border-orange-300' },
                    { value: 'likely',        label: 'Likely',            activeBg: 'bg-lime-100 border-lime-400 text-lime-700',       hoverBorder: 'hover:border-lime-300' },
                    { value: 'very_likely',   label: 'Very Likely',       activeBg: 'bg-emerald-100 border-emerald-400 text-emerald-700', hoverBorder: 'hover:border-emerald-300' },
                  ].map(opt => {
                    const isActive = confidenceTernary === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setConfidenceTernary(isActive ? null : opt.value)}
                        className={`
                          flex-1 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all duration-200
                          ${isActive
                            ? opt.activeBg
                            : `bg-white border-gray-300 text-gray-500 ${opt.hoverBorder} hover:text-gray-700`}
                        `}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

          </div>

          {/* ======== Formulation — conditions under consideration + notes ======== */}
          <div className={`${REVIEW_COLUMN_CLASS} bg-white rounded-2xl border border-gray-200 p-5`}>
            <h3 className="text-sm font-bold text-gray-800 mb-0.5">Differentials</h3>
            <p className="text-xs text-gray-500 mb-3">
              Select any conditions you are currently considering. Add notes for each to capture your clinical reasoning.
            </p>

            {/* Condition picker — pill buttons + "Other" inline input */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[...FORMULATION_CONDITIONS, ...customConditions].map(condition => {
                const isSelected = formulationConditions.includes(condition) && !leavingConditions.has(condition);
                return (
                  <button
                    key={condition}
                    onClick={() => toggleCondition(condition)}
                    className={`
                      inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                      border transition-all duration-200
                      ${isSelected
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-primary/50 hover:text-primary'}
                    `}
                  >
                    {!isSelected && <PlusIcon className="w-3 h-3" />}
                    {isSelected && (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {condition}
                  </button>
                );
              })}

              {/* "Other" button — morphs into an inline text input when clicked */}
              <div
                className={`
                  inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold border
                  transition-all duration-300 overflow-hidden
                  ${otherInputOpen
                    ? 'bg-primary/10 border-primary text-primary px-2 py-0.5'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-primary/50 hover:text-primary px-3 py-1.5 cursor-pointer'}
                `}
                onClick={() => {
                  if (!otherInputOpen) {
                    setOtherInputOpen(true);
                    requestAnimationFrame(() => otherInputRef.current?.focus());
                  }
                }}
              >
                {!otherInputOpen && <PlusIcon className="w-3 h-3" />}
                {!otherInputOpen && <span>Other</span>}
                {otherInputOpen && (
                  <input
                    ref={otherInputRef}
                    type="text"
                    value={otherDraft}
                    onChange={(e) => setOtherDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); confirmOtherCondition(); }
                      if (e.key === 'Escape') { setOtherDraft(''); setOtherInputOpen(false); }
                    }}
                    onBlur={() => { if (otherDraft.trim()) confirmOtherCondition(); else setOtherInputOpen(false); }}
                    placeholder="Type condition name…"
                    className="bg-transparent text-xs font-semibold text-primary placeholder-primary/40
                      border-0 outline-none w-48 py-1"
                  />
                )}
              </div>
            </div>

            {/* Selected conditions — two-column grid with animated rows */}
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                display: 'grid',
                gridTemplateRows: formulationConditions.length > 0 ? '1fr' : '0fr',
                opacity: formulationConditions.length > 0 ? 1 : 0,
              }}
            >
              <div className="min-h-0">
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  {formulationConditions.map(condition => {
                    const isLeaving = leavingConditions.has(condition);
                    return (
                      <div
                        key={condition}
                        className={`grid gap-4 items-start ${
                          isLeaving
                            ? 'animate-[fadeSlideOut_0.2s_ease-in_forwards]'
                            : 'animate-[fadeSlideIn_0.25s_ease-out]'
                        }`}
                        style={{ gridTemplateColumns: '220px 1fr' }}
                        onAnimationEnd={() => { if (isLeaving) finishLeaving(condition); }}
                      >
                        <div className="flex items-center gap-2 pt-1.5">
                          <button
                            onClick={() => toggleCondition(condition)}
                            className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                            title="Remove condition"
                          >
                            <XIcon className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-sm font-semibold text-gray-800 leading-snug">{condition}</span>
                        </div>
                        <StructuredNotesEditor
                          notes={formulationConditionNotes[condition] || []}
                          onChange={(val) => handleConditionNoteChange(condition, val)}
                          placeholder="Type a note and press Enter…"
                          minHeight="2.5rem"
                          domainId={condition}
                          onMoveNoteIn={handleMoveFormulationNote}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* ======== Confidential Case Notes — separate panel ======== */}
          <div
            className={`${REVIEW_COLUMN_CLASS} rounded-2xl border border-gray-200 p-5`}
            style={{ backgroundColor: '#ced6f6' }}
          >
            <h3 className="text-sm font-bold text-gray-800 mb-0.5">Confidential Case Notes</h3>
            <p className="text-xs text-gray-500 italic mb-2">These notes will not be used as inputs for AI-generated drafts or included in the patient report.</p>
            <StructuredNotesEditor
              notes={formulationNotes}
              onChange={handleNotesChange}
              placeholder="Record clinical reasoning, differential considerations, or synthesis notes…"
              rows={6}
            />
          </div>

        </div>
        </div>
      </div>

      {/* ---- Detail popup overlay (symptoms + domains) ---- */}
      {detailPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDetailPopup(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[90vw] max-h-[80vh] overflow-y-auto text-left animate-[fadeScaleIn_0.15s_ease-out]">
            <button
              onClick={() => setDetailPopup(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>

            {/* ---- Symptom detail popup ---- */}
            {detailPopup.type === 'symptom' && (
              <>
                <h2 className="text-base font-bold text-gray-900 mb-0.5">
                  {detailPopup.symptomId}. {detailPopup.shortTitle}
                </h2>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-gray-500">{detailPopup.stage}</span>
                  <EvidencePill value={detailPopup.evidence} />
                </div>

                {/* Notes — direct quotes within the text are highlighted inline */}
                {detailPopup.notes && detailPopup.notes.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Notes</h3>
                    <ul className="space-y-1.5">
                      {detailPopup.notes.map((note) => (
                        <li key={note.id} className="flex items-start gap-2">
                          <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                          <span className="text-sm text-gray-700 leading-relaxed">{renderNoteWithQuotes(note.text)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Questions marked as asked */}
                {detailPopup.askedQuestions.length > 0 && (
                  <div>
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Questions Marked as Asked</h3>
                    <div className="flex flex-col gap-1.5">
                      {detailPopup.askedQuestions.map((q, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          {q}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ---- Domain detail popup ---- */}
            {detailPopup.type === 'domain' && (
              <>
                <h2 className="text-base font-bold text-gray-900 mb-0.5">
                  {detailPopup.domainTitle}
                </h2>
                <p className="text-xs text-gray-500 mb-4">{detailPopup.stage} — Evidence breakdown by DSM criteria</p>

                {EVIDENCE_PRIORITY.map(level => {
                  const symptoms = detailPopup.symptomBuckets[level];
                  if (!symptoms || symptoms.length === 0) return null;
                  const pill = EVIDENCE_PILL[level];
                  return (
                    <div key={level} className="mb-3 last:mb-0">
                      <div className="mb-1">
                        <span className={`inline-block px-2.5 py-0.5 rounded-lg text-[11px] font-semibold ${pill?.bg || 'bg-gray-100'} ${pill?.text || 'text-gray-600'}`}>
                          {level}
                        </span>
                      </div>
                      <ul className="space-y-0.5 pl-1">
                        {symptoms.map(s => (
                          <li key={s} className="flex items-start gap-2">
                            <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">•</span>
                            <span className="text-sm text-gray-700 leading-snug">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* Keyframes for animations */}
      <style>{`
        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideOut {
          from { opacity: 1; transform: translateY(0); max-height: 200px; }
          to   { opacity: 0; transform: translateY(-6px); max-height: 0; }
        }
      `}</style>

    </Layout>
  );
}
